"use client";

import { Tag } from "@pamperedchef/design-system";
import type { CatalogLabel, LabelTheme } from "@/domain/catalog";
import * as styles from "./ProductUrgentMessageLabel.module.scss";

export interface ProductUrgentMessageLabelProps {
	/**
	 * `kind: "urgent_message"` labels from `CatalogProduct.labels` +
	 * `scheduledLabels` (date-window filtered upstream). Caller filters
	 * by kind; this composite stays presentational.
	 */
	labels: CatalogLabel[];
	className?: string;
}

const THEME_TO_DS_COLOR: Record<
	LabelTheme,
	"success" | "info" | "warning" | "error" | "grey"
> = {
	default: "grey",
	info: "info",
	success: "success",
	warning: "warning",
	alert: "error"
};

/**
 * Inline-row of `Tag` chips that sit just above (or beside) the price block:
 * Best Seller, Host Exclusive, New This Month, etc. Wraps to a second line
 * on narrow viewports. Each label's `theme` maps to a DS Tag color.
 */
export function ProductUrgentMessageLabel({
	labels,
	className
}: ProductUrgentMessageLabelProps) {
	if (!labels.length) return null;

	return (
		<div
			className={[styles["urgent-label"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="ProductUrgentMessageLabel"
		>
			{labels.map((label, idx) => (
				<Tag
					key={`${label.label}-${idx}`}
					label={label.label}
					icon={label.icon ? { name: label.icon } : undefined}
					kind="subtle"
					size="md"
					color={THEME_TO_DS_COLOR[label.theme ?? "default"]}
				/>
			))}
		</div>
	);
}
